import React from "react";
import {
  IconButton,
  Badge,
  Popper,
  Fade,
  Box,
  Typography,
  FormGroup,
  FormControlLabel,
  Switch,
  Stack,
  Alert,
  Button,
  useTheme,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faBellSlash, faCheck, faCheckDouble } from "@fortawesome/free-solid-svg-icons";
import { useNotifications } from "../Context/NotificationContext"; // Adjust this path

const NotificationCenter = () => {
  const { notifications, markAllAsRead, markAsRead, unreadCount } = useNotifications();
  const [showUnreadOnly, setShowUnreadOnly] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const theme = useTheme();

  const toggleNotificationCenter = (event) => {
    setAnchorEl(event.currentTarget);
    setIsOpen(!isOpen);
  };

  const toggleFilter = (e) => {
    setShowUnreadOnly(!showUnreadOnly);
  };

  return (
    <>
      <IconButton
        style={{
          color: theme.palette.info.main,
          boxShadow: isOpen ? `0px 0px 8px ${theme.palette.primary.main}` : "none", // Highlight the icon when open
        }}
        onClick={toggleNotificationCenter}
      >
        <Badge badgeContent={unreadCount} color={isOpen ? "secondary" : "success"}>
          <FontAwesomeIcon icon={isOpen ? faBellSlash : faBell} /> {/* Change icon when open */}
        </Badge>
      </IconButton>

      <Popper
        sx={{ zIndex: 5000 }}
        open={isOpen}
        anchorEl={anchorEl}
        placement="bottom-end" // Align popover to the bottom-right of the bell icon
        transition
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Box
              sx={{
                marginTop: "8px", // Small gap between icon and popover
                padding: "16px",
                backgroundColor: theme.palette.background.default,
                borderRadius: "12px",
                borderColor: theme.palette.primary.main,
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.7)",
                color: theme.palette.info.main,
                minWidth: "300px",
                zIndex: 5000,
              }}
            >
              <Typography variant="h6" style={{ color: theme.palette.primary.main }}>
                Notifications
              </Typography>

              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      onChange={toggleFilter}
                      checked={showUnreadOnly}
                    />
                  }
                  label="Show unread only"
                  style={{ color: theme.palette.success.text }}
                />
              </FormGroup>

              <Stack spacing={2} sx={{ maxHeight: "300px", overflowY: "auto" }}>
                {(!notifications.length || (unreadCount === 0 && showUnreadOnly)) && (
                  <Typography style={{ color: theme.palette.success.text }}>
                    No notifications yet!
                  </Typography>
                )}
                {(showUnreadOnly
                  ? notifications.filter((v) => !v.read)
                  : notifications
                ).map((notification) => (
                  <Alert
                    key={notification.id}
                    severity={notification.read ? "success" : "info"}
                    sx={{
                      backgroundColor: notification.read
                        ? theme.palette.secondary.main
                        : theme.palette.primary.main,
                      color: theme.palette.success.text,
                      borderRadius: "8px",
                    }}
                    action={
                      notification.read ? (
                        <FontAwesomeIcon icon={faCheck} style={{ color: theme.palette.info.main }} />
                      ) : (
                        <IconButton onClick={() => markAsRead(notification.id)}>
                          <FontAwesomeIcon icon={faCheckDouble} style={{ color: theme.palette.info.main }} />
                        </IconButton>
                      )
                    }
                  >
                    <Typography variant="subtitle1" style={{ fontWeight: "bold", color: theme.palette.info.main }}>
                      {notification.title}
                    </Typography>
                    <Typography variant="body2" style={{ color: theme.palette.info.main }}>
                      {notification.content}
                    </Typography>
                  </Alert>
                ))}
              </Stack>

              <Box mt={2} display="flex" justifyContent="space-between">
                <Button
                  variant="contained"
                  onClick={markAllAsRead}
                  style={{
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.info.main,
                  }}
                >
                  Mark all as read
                </Button>
              </Box>
            </Box>
          </Fade>
        )}
      </Popper>
    </>
  );
};

export default NotificationCenter;
